<template>
  <div v-if="isActive" class="form-part">
    <component :is="titleTag" v-if="title">{{ title }}</component>
    <slot />
    <ak-field grouped>
      <ak-button
        v-if="!isResponse && showBack"
        color="grey-lightest"
        class="color-grey-light"
      >
        {{ backText }}
      </ak-button>
      <ak-button v-if="!isResponse" type="submit">
        {{ submitText }}
      </ak-button>
    </ak-field>
    <div v-if="pageNumber" class="clearfix">
      <small :class="pagePositionClass">
        {{ currentPage }}
      </small>
    </div>
  </div>
</template>

<script>
import { defineComponent, computed, inject, onMounted } from 'vue'
import { uuid, isObject } from '../../utils/helpers'
import Field from '../field/Field.vue'
import Button from '../button/Button.vue'

export default defineComponent({
  /**
   * @type {String}
   * @description This is the name of the component this will also
   * be the tag name of the component to use in templates.
   * @example
   * <ak-form-part></ak-form-part>
   */
  name: 'AkFormPart',

  /**
   * @type {Object}
   * @description A collection of components that this component
   * is dependant on.
   */
  components: {
    [Field.name]: Field,
    [Button.name]: Button,
  },

  /**
   * @return Object
   * @description Provides the returned object to the components decendants.
   */
  provide() {
    return {
      akFormPart: this,
    }
  },

  /**
   * @type {Object}
   * @description A collection of properties that may be passed into
   * the component these will be bound to the component instance and
   * may be access in the template and component methods.
   */
  props: {
    /**
     * @type {String}
     * @description The title to output for the form part.
     */
    title: {
      type: String,
      default: undefined,
    },

    /**
     * @type {String}
     * @description The title element type to render.
     */
    titleTag: {
      type: String,
      default: 'h1',
    },

    /**
     * @type {String}
     * @description The text to display in the submit button.
     */
    submitText: {
      type: String,
      default: 'Submit',
    },

    /**
     * @type {Boolean}
     * @description Determines whether or not to display the
     * back button for the form part.
     */
    showBack: {
      type: Boolean,
      default: false,
    },

    /**
     * @type {String}
     * @description The text to display in the back button.
     */
    backText: {
      type: String,
      default: 'Back',
    },

    /**
     * @type {Boolean}
     * @description Determines if the form part is a response page
     * this will force hide any submit or back buttons.
     */
    isResponse: {
      type: Boolean,
      default: false,
    },

    /**
     * @type {Boolean}
     * @description Determines whether or not to display the current
     * page number in the bottom of the form part.
     */
    pageNumber: {
      type: Boolean,
      default: false,
    },

    /**
     * @type {String}
     * @description Determines the position of the page count.
     */
    pagePosition: {
      validator: (i) => ['left', 'right'].indexOf(i) >= 0,
      type: String,
      default: 'right',
    },

    fields: {
      type: Array,
      default: () => [],
    },
  },

  /**
   * @param {Object} props - The properties of the component
   * @param {Object} options - The setup options
   * @param {Function} options.emit - The emit method for triggering events
   * @param {Function} options.attrs - The attributes for the component.
   * @description Sets up the component and returns an object containing all of the
   * attributes for the component to use.
   */
  setup(props) {
    const parent = inject('akForm')

    /**
     * @returns {Boolean}
     * @description Returns a boolean flag to determine if the part
     * is currently active in the parent form.
     */
    const isActive = computed(() => {
      return (
        isObject(parent) &&
        parent.activePart &&
        parent.activePart.uid === inst.uid
      )
    })

    /**
     * @returns {String}
     * @description Returns the current page count.
     */
    const currentPage = computed(() => {
      return parent.currentPage
    })

    /**
     * @returns {String}
     * @description Returns the position class for the page count.
     */
    const pagePositionClass = computed(() => {
      return `pull-${props.pagePosition}`
    })

    const inst = {
      uid: uuid(),
      isActive,
      currentPage,
      pagePositionClass,
    }

    onMounted(() => {
      if (isObject(parent) && parent.isForm) {
        parent.registerPart(inst)
      }
    })

    return inst
  },
})
</script>
